import {
  COOKIE_POLICY_STORAGE_KEY,
  COOKIE_API_STATUS_STORAGE_KEY,
  COOKIE_POLICY_MINIMUM_DATE,
  storageMap,
  CookieGroupOption,
} from './constants';

type CookieBrowserPreferencesArguments = {
  mandatory: true;
  functional: boolean;
  analytical: boolean;
};

export type CookieBrowserPreferencesReturn = {
  mandatory: true;
  functional: boolean;
  analytical: boolean;
  lastModified: number;
};

type SetCookieAccordingToBrowserPreferencesArguments = {
  cookieKey: string;
  cookieValue: string | boolean | any;
  isPersistent?: boolean;
};

type SetCookieAccordingToCookieGroupPreferencesArguments = {
  cookieKey: string;
  cookieValue: string | boolean;
  isPersistent?: boolean;
  cookieGroup: CookieGroupOption;
};

export class CookieError extends Error {
  errorCode: number;
  constructor(errorCode: number, message: string) {
    super(message);
    this.errorCode = errorCode;
  }
}

export class CookieUtils {
  platform: string;
  launchCookieModal: any;
  #heldFunction: any | null;
  constructor(platform: string) {
    this.platform = platform;
  }

  setLaunchCookieModal = (launchCookieModal: any) => {
    this.launchCookieModal = launchCookieModal;
  };

  getPersistence = () => {
    return !!this.getCookieValue(
      `${this.platform === 'zeus' ? 'wa-' : ''}persistence`,
      true,
    );
  };

  validateCookiePreferenceValue = (potentialPreferences: any) => {
    if (typeof potentialPreferences !== 'object') return false;
    if (potentialPreferences === null) return false;
    const fields = ['mandatory', 'functional', 'analytical', 'lastModified'];
    for (const field of fields) {
      if (field === 'lastModified') {
        if (typeof potentialPreferences[field] !== 'number') return false;
        if (
          potentialPreferences[field] <
          COOKIE_POLICY_MINIMUM_DATE[this.platform]
        ) {
          return false;
        }
      } else {
        if (potentialPreferences[field] === undefined) return false;
        if (potentialPreferences[field] === null) return false;
        if (potentialPreferences[field] === '') return false;
        if (typeof potentialPreferences[field] !== 'boolean') return false;
      }
    }
    return true;
  };

  fetchApiStatusBrowserPreference = (): boolean => {
    const rawValue = localStorage.getItem(COOKIE_API_STATUS_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : true;
  };

  setApiStatusBrowserPreference = (shouldSendApiRequest: boolean): boolean => {
    localStorage.setItem(
      COOKIE_API_STATUS_STORAGE_KEY,
      JSON.stringify(shouldSendApiRequest),
    );
    return shouldSendApiRequest;
  };

  /**
   * Fetch the cookie preferences from browser localStorage
   * @throws Will throw an error if there is no valid Cookie Preference values.
   * @returns {CookieBrowserPreferencesReturn}
   */
  fetchCookieBrowserPreferences = (): CookieBrowserPreferencesReturn => {
    const rawValue = localStorage.getItem(COOKIE_POLICY_STORAGE_KEY);
    if (rawValue) {
      const storedValue = JSON.parse(rawValue);
      if (this.validateCookiePreferenceValue(storedValue)) {
        return storedValue;
      }
    }
    throw new CookieError(0, 'Invalid cookie preferences');
  };

  setCookieBrowserPreferences = (
    cookieBrowserPreferences: CookieBrowserPreferencesArguments,
  ): CookieBrowserPreferencesReturn => {
    const objectToStore = {
      ...cookieBrowserPreferences,
      lastModified: Date.now(),
    } as const;
    localStorage.setItem(
      COOKIE_POLICY_STORAGE_KEY,
      JSON.stringify(objectToStore),
    );
    window.dispatchEvent(new Event('cookiePreferencesModalClosed'));
    this.setApiStatusBrowserPreference(true);
    this.removeCookiesByGroupIfGroupDisabled('functional');

    if (
      this.#heldFunction &&
      this.#heldFunction !== null &&
      this.#heldFunction !== undefined
    ) {
      this.#heldFunction();
      this.#heldFunction = null;
    }
    return objectToStore;
  };

  isKeyValid = (
    cookieKey: string,
    cookieBrowserPreferences: CookieBrowserPreferencesReturn = this.fetchCookieBrowserPreferences(),
    cookieGroupValidator: (
      CookieGroupOption: CookieGroupOption,
      cookieBrowserPreferences: CookieBrowserPreferencesReturn,
    ) => boolean = this.isCookieGroupValid,
  ): boolean => {
    if (!cookieBrowserPreferences) return false;
    const sMap = storageMap();
    if (
      cookieBrowserPreferences.analytical === true &&
      cookieBrowserPreferences.functional === true
    ) {
      return true;
    }
    if (
      sMap[this.platform].mandatory.findIndex(
        mandatoryCookieKey => cookieKey === mandatoryCookieKey,
      ) >= 0
    ) {
      return cookieGroupValidator('mandatory', cookieBrowserPreferences);
    }
    if (
      sMap[this.platform].functional.findIndex(
        functionalCookieKey => cookieKey === functionalCookieKey,
      ) >= 0
    ) {
      return cookieGroupValidator('functional', cookieBrowserPreferences);
    }
    if (
      sMap[this.platform].analytical.findIndex(
        analyticalCookieKey => cookieKey === analyticalCookieKey,
      ) >= 0
    ) {
      return cookieGroupValidator('analytical', cookieBrowserPreferences);
    }
    return false;
  };

  isCookieGroupValid = (
    cookieGroup: CookieGroupOption,
    cookieBrowserPreferences: CookieBrowserPreferencesReturn = this.fetchCookieBrowserPreferences(),
  ): boolean => cookieBrowserPreferences[cookieGroup];

  removeCookiesByGroupIfGroupDisabled = (
    cookieGroup: CookieGroupOption,
  ): void => {
    if (!this.isCookieGroupValid(cookieGroup)) {
      const sMap = storageMap();

      sMap[this.platform][cookieGroup].forEach(cookieName => {
        sessionStorage.removeItem(cookieName);
        localStorage.removeItem(cookieName);
      });
    }
  };

  getCookieValue = (cookieKey: string, isPersistent: boolean) => {
    let persistent;
    if (typeof isPersistent === 'boolean') {
      persistent = isPersistent;
    } else {
      persistent = this.getPersistence();
    }
    return persistent
      ? localStorage.getItem(cookieKey)
      : sessionStorage.getItem(cookieKey);
  };

  checkBrowserPreferencesForCookieGroupHOF =
    (cookieGroup: CookieGroupOption) =>
    (setCookieFunction: any): void => {
      this.#heldFunction = () =>
        this.checkBrowserPreferencesForCookieGroupHOF(cookieGroup)(
          setCookieFunction,
        );
      try {
        if (this.isCookieGroupValid(cookieGroup)) {
          setCookieFunction();
        }
        this.#heldFunction = null;
      } catch {
        this.launchCookieModal();
      }
    };

  checkBrowserPreferencesForCookieKeyHOF =
    (cookieKey: string) =>
    (setCookieFunction: any): void => {
      this.#heldFunction = () =>
        this.checkBrowserPreferencesForCookieKeyHOF(cookieKey)(
          setCookieFunction,
        );
      try {
        if (this.isKeyValid(cookieKey)) {
          setCookieFunction();
        }
        this.#heldFunction = null;
      } catch {
        this.launchCookieModal();
      }
    };

  setCookieAccordingToBrowserPreferences = ({
    cookieKey,
    cookieValue,
    isPersistent,
  }: SetCookieAccordingToBrowserPreferencesArguments) => {
    let persistent;
    if (typeof isPersistent === 'boolean') {
      persistent = isPersistent;
    } else {
      persistent = this.getPersistence();
    }
    const saveToBrowserStorage = () =>
      persistent
        ? localStorage.setItem(cookieKey, JSON.stringify(cookieValue))
        : sessionStorage.setItem(cookieKey, JSON.stringify(cookieValue));

    this.#heldFunction = () =>
      this.setCookieAccordingToBrowserPreferences({
        cookieKey,
        cookieValue,
        isPersistent,
      });
    try {
      if (this.isKeyValid(cookieKey)) {
        saveToBrowserStorage();
      }
      this.#heldFunction = null;
    } catch {
      this.launchCookieModal();
    }
  };

  setCookieAccordingToCookieGroupPreferences = ({
    cookieKey,
    cookieValue,
    isPersistent,
    cookieGroup,
  }: SetCookieAccordingToCookieGroupPreferencesArguments) => {
    let persistent;
    if (typeof isPersistent === 'boolean') {
      persistent = isPersistent;
    } else {
      persistent = this.getPersistence();
    }
    const saveToBrowserStorage = () =>
      persistent
        ? localStorage.setItem(cookieKey, JSON.stringify(cookieValue))
        : sessionStorage.setItem(cookieKey, JSON.stringify(cookieValue));

    this.#heldFunction = () =>
      this.setCookieAccordingToCookieGroupPreferences({
        cookieKey,
        cookieValue,
        isPersistent,
        cookieGroup,
      });
    try {
      if (this.isCookieGroupValid(cookieGroup)) {
        saveToBrowserStorage();
      }
      this.#heldFunction = null;
    } catch {
      this.launchCookieModal();
    }
  };
}
